import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { title: "Contact — Mathys Gautier" },
      {
        name: "description",
        content:
          "Contactez Mathys Gautier pour des idées de reportages, des propositions de pige, des collaborations data, des interviews ou des questions sur ses travaux.",
      },
      { name: "robots", content: "noimageindex" },
      { property: "og:title", content: "Contact — Mathys Gautier" },

      {
        property: "og:description",
        content: "Contactez Mathys Gautier pour des idées de reportages, des propositions de pige, des collaborations data, des interviews ou des questions sur ses travaux.",
      },
      { property: "og:url", content: "https://mathysgautier.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://mathysgautier.com/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact — Mathys Gautier",
          description:
            "Contactez Mathys Gautier pour des idées de reportages, des propositions de pige, des collaborations data, des interviews ou des questions sur ses travaux.",
          url: "https://mathysgautier.com/contact",
          isPartOf: {
            "@type": "WebSite",
            name: "Mathys Gautier",
            url: "https://mathysgautier.com",
          },
          about: {
            "@type": "Person",
            name: "Mathys Gautier",
            jobTitle: "Journaliste",
            url: "https://mathysgautier.com",
            sameAs: ["https://www.linkedin.com/in/mathys-gautier", "https://x.com/mathysgtr"],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Accueil",
              item: "https://mathysgautier.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Contact",
              item: "https://mathysgautier.com/contact",
            },
          ],
        }),
      },
    ],
  }),
  component: Contact,
});

const SUBJECT_OPTIONS = [
  "Idée de reportage",
  "Proposition de pige",
  "Proposition de collaboration sur un sujet data",
  "Question ou remarque",
  "Autre",
];

function Contact() {
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [otherSubject, setOtherSubject] = useState("");
  const [errors, setErrors] = useState<{
    name?: string;
    phone?: string;
    message?: string;
  }>({});

  const resetForm = () => {
    setSent(false);
    setIsLoading(false);
    setSubject("");
    setOtherSubject("");
    setErrors({});
    const form = document.getElementById("contact-form") as HTMLFormElement | null;
    form?.reset();
  };

  return (
    <section className="mx-auto max-w-5xl px-6 pt-16 pb-8 sm:pb-24">
      <div className="grid gap-10 lg:grid-cols-[1fr_1px_1fr] lg:gap-16">
        <div>
          <p className="eyebrow text-ocean mb-12">Contact</p>
          <h1 className="mt-12 font-display text-5xl leading-[1.05] sm:text-6xl">
            Une piste, <em className="text-ocean">une idée,</em> un projet&nbsp;?
          </h1>
          <p className="mt-16 max-w-md text-base leading-relaxed text-muted-foreground">
            J’accueille avec beaucoup de plaisir les propositions de collaboration, de reportage ou même les questions !
            <br />
            Je réponds en général sous 1 à 3 jours.
          </p>

          <a
            href="https://www.linkedin.com/in/mathys-gautier"
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-6 inline-flex items-center gap-3 rounded-full border border-border bg-background/60 px-4 py-2 text-sm text-foreground/80 backdrop-blur transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-ocean hover:text-ocean hover:shadow-[0_6px_24px_-14px_rgba(25,121,172,0.5)]"
          >
            <svg
              aria-hidden
              viewBox="0 0 24 24"
              className="h-4 w-4 fill-current text-[#0A66C2] group-hover:text-[#0A66C2]"
            >
              <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .78 0 1.73v20.53C0 23.22.79 24 1.77 24h20.44c.98 0 1.78-.78 1.78-1.74V1.73C24 .78 23.2 0 22.22 0z" />
            </svg>
            <span>Me suivre sur LinkedIn</span>
          </a>

          <a
            href="https://x.com/mathysgtr?s=11"
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-4 ml-0 inline-flex items-center gap-3 rounded-full border border-border bg-background/60 px-4 py-2 text-sm text-foreground/80 backdrop-blur transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-ocean hover:text-ocean hover:shadow-[0_6px_24px_-14px_rgba(25,121,172,0.5)] sm:ml-3"
          >
            <svg aria-hidden viewBox="0 0 24 24" className="h-4 w-4 fill-current">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
            </svg>
            <span>Me suivre sur X</span>
          </a>

          <div className="mt-10 max-w-md space-y-4 text-sm leading-relaxed text-muted-foreground">
            <h2 className="font-serif text-lg text-foreground">Pour quels sujets m’écrire&nbsp;?</h2>
            <ul className="list-disc space-y-1 pl-5">
              <li>Idées de reportages ou de sujets d’enquête à creuser ensemble.</li>
              <li>Propositions de pige pour une rédaction.</li>
              <li>Collaborations data : nettoyage, analyse et mise en forme de jeux de données.</li>
              <li>Interviews, interventions et questions sur mes différents travaux publiés.</li>
            </ul>
            <p>
              Avant de me contacter, vous pouvez parcourir{" "}
              <Link to="/portfolio" className="text-ocean underline underline-offset-4">
                mes réalisations journalistiques
              </Link>
              , mes{" "}
              <Link to="/dataviz" className="text-ocean underline underline-offset-4">
                visualisations de données
              </Link>{" "}
              ou{" "}
              <Link to="/parcours" className="text-ocean underline underline-offset-4">
                mon parcours professionnel
              </Link>
              . Le formulaire ci-contre vous permettra, une fois rempli, d'ouvrir directement votre logiciel de
              messagerie avec un message pré-rempli.
            </p>
          </div>
        </div>

        {/* Séparateur : trait horizontal en mobile, vertical en desktop */}
        <div className="h-px w-full bg-border lg:h-full lg:w-px" aria-hidden />

        <form
          id="contact-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            setIsLoading(true);
            const formData = new FormData(e.currentTarget);
            const name = String(formData.get("name") ?? "").trim();
            const phoneRaw = String(formData.get("phone") ?? "").trim();
            const subjectValue = String(formData.get("subject") ?? "");
            const subjectOther = String(formData.get("subject_other") ?? "").trim();
            const message = String(formData.get("message") ?? "").trim();

            const newErrors: { name?: string; phone?: string; message?: string } = {};
            if (name.length === 0) {
              newErrors.name = "Veuillez saisir votre nom et prénom.";
            }
            const phoneDigits = phoneRaw.replace(/\D/g, "");
            if (phoneRaw.length === 0) {
              newErrors.phone = "Veuillez saisir votre numéro de téléphone.";
            } else if (!/^[\d\s\-\.\+\(\)]{7,}$/.test(phoneRaw) || phoneDigits.length < 7) {
              newErrors.phone = "Le numéro doit contenir au moins 7 chiffres.";
            }
            if (message.length <= 30) {
              newErrors.message = "Le message doit contenir plus de 30 caractères.";
            }

            setErrors(newErrors);
            if (Object.keys(newErrors).length > 0) {
              setIsLoading(false);
              return;
            }

            const finalSubject = subjectValue === "Autre" && subjectOther ? subjectOther : subjectValue;

            const signature = `\n\n—\n${name}\nTél. : ${phoneRaw}`;
            const body = `${message}${signature}`;

            const mailto =
              `mailto:mathys.gautier@sciencespo.fr` +
              `?subject=${encodeURIComponent(finalSubject)}` +
              `&body=${encodeURIComponent(body)}`;

            window.location.href = mailto;
            setSent(true);
            setIsLoading(false);
          }}
          className="relative rounded-3xl border border-border bg-background p-8 shadow-[0_20px_60px_-30px_rgba(28,42,63,0.25)] sm:p-10"
        >
          <div className="space-y-6">
            <Field label="Nom et prénom" name="name" type="text" required error={errors.name} />
            <Field
              label="Téléphone"
              name="phone"
              type="tel"
              inputMode="tel"
              pattern="[\d\s\-\.\+\(\)]{7,}"
              required
              error={errors.phone}
            />

            <div>
              <label className="eyebrow" htmlFor="subject">
                Objet <RequiredMark />
              </label>
              <select
                id="subject"
                name="subject"
                required
                aria-label="Objet du message"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-2 w-full border-0 border-b border-border bg-transparent py-2 text-sm outline-none transition-all duration-200 focus:border-foreground focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="" disabled>
                  Choisir une raison…
                </option>
                {SUBJECT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            {subject === "Autre" && (
              <Field
                label="Précisez"
                name="subject_other"
                type="text"
                required
                value={otherSubject}
                onChange={(e) => setOtherSubject(e.target.value)}
              />
            )}
            <div>
              <label className="eyebrow" htmlFor="message">
                Message <RequiredMark />
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                className="mt-2 w-full border-0 border-b border-border bg-transparent py-2 text-sm outline-none transition-all duration-200 focus:border-foreground focus-visible:ring-2 focus-visible:ring-ring"
              />
              {errors.message && <p className="mt-2 text-xs text-red-500">{errors.message}</p>}
            </div>
            <Button
              type="submit"
              isLoading={isLoading}
              className="w-full rounded-full bg-ocean px-6 py-3 text-sm text-paper hover:bg-ocean-700"
            >
              Envoyer
            </Button>
          </div>

          {sent && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/95 p-8 backdrop-blur-sm animate-in fade-in duration-300">
              <div className="max-w-sm rounded-2xl border border-border bg-background p-10 text-center shadow-lg">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ocean/10 text-ocean">
                  <Check className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="mt-6 space-y-6 text-center text-sm text-muted-foreground">
                  <p className="font-serif text-base font-semibold text-foreground">
                    Une dernière étape…
                  </p>
                  <p>
                    Votre logiciel de messagerie s’est ouvert avec le message pré-rempli. Il ne
                    reste plus qu’à cliquer sur « Envoyer » dans votre application.
                  </p>
                  <p>
                    Si cela ne fonctionne pas, envoyez un mail directement à{" "}
                    <a
                      href="mailto:mathys.gautier@sciencespo.fr"
                      className="text-ocean underline underline-offset-4 decoration-ocean decoration-2"
                    >
                      mathys.gautier@sciencespo.fr
                    </a>
                    .
                  </p>
                  <p>Je vous recontacterai d’ici peu !</p>
                </div>
                <div className="mt-10 flex flex-col items-center gap-5">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="nav-link inline-flex items-center gap-2 text-sm micro-interactive active:scale-95"
                  >
                    Envoyer un autre message
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate({ to: "/" })}
                    className="text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground micro-interactive active:scale-95"
                  >
                    Revenir à la page d’accueil
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

function RequiredMark() {
  return <span className="ml-0.5 text-ocean">*</span>;
}

function Field({
  label,
  name,
  type,
  inputMode,
  pattern,
  required,
  value,
  onChange,
  error,
}: {
  label: string;
  name: string;
  type: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  pattern?: string;
  required?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
}) {
  return (
    <div>
      <label className="eyebrow" htmlFor={name}>
        {label} {required && <RequiredMark />}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        inputMode={inputMode}
        pattern={pattern}
        required={required}
        value={value}
        onChange={onChange}
        className="mt-2 w-full border-0 border-b border-border bg-transparent py-2 text-sm outline-none transition-all duration-200 focus:border-foreground focus-visible:ring-2 focus-visible:ring-ring"
      />
      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
    </div>
  );
}
